import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import mermaid from 'mermaid';
import { cn } from '../lib/utils';
import { applyPlatformFavicon, applySpaceFavicon } from '../lib/favicon';
import { resolveSpaceLogoUrl, resolveSpaceFaviconUrl } from '../lib/spaceBranding';
import { readDocsThemePreference, writeDocsThemePreference } from '../lib/docsThemePreference';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import DocumentFeedback from '../components/documents/DocumentFeedback';
import PublicSearchModal from '../components/search/PublicSearchModal';
import { VideoBlockPreview, FileBlockPreview } from '../components/documents/MediaBlockPreview';
import { API_URL } from '../config';
import {
  Search,
  ChevronRight,
  ChevronDown,
  FileText,
  Loader2,
  Menu,
  X,
  Moon,
  Sun,
  Command,
} from 'lucide-react';

// Initialize mermaid for public page
mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'loose',
  fontFamily: 'inherit'
});

// Interactive Tabs Component for public preview
const InteractiveTabs = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  if (!tabs || tabs.length === 0) return null;
  
  return (
    <div className="mb-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid="public-tabs">
      <div className="flex gap-1 p-1 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded transition-colors',
              i === activeTab 
                ? 'bg-white dark:bg-slate-900 shadow-sm text-blue-600 dark:text-blue-400' 
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            )}
          >
            {tab.title || `Tab ${i + 1}`}
          </button>
        ))}
      </div>
      <div className="p-4 text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: tabs[activeTab]?.content || '' }} />
    </div>
  );
};

// Interactive Accordion Component for public preview
const InteractiveAccordion = ({ title, content, isOpen: initialOpen }) => {
  const [isOpen, setIsOpen] = useState(initialOpen || false);
  
  return (
    <div className="mb-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid="public-accordion">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <span className="font-medium text-slate-900 dark:text-white">{title || 'Accordion'}</span>
        <ChevronDown className={cn('w-5 h-5 text-slate-500 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="px-4 py-3 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: content || '' }} />
      )}
    </div>
  );
};

// Mermaid Diagram Component
const MermaidDiagram = ({ code, caption }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!code || !containerRef.current) return;
    
    const renderDiagram = async () => {
      try {
        const id = `mermaid-public-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, code);
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          // Style the SVG
          const svgEl = containerRef.current.querySelector('svg');
          if (svgEl) {
            svgEl.style.maxWidth = '100%';
            svgEl.style.height = 'auto';
          }
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Invalid Mermaid syntax');
      }
    };
    
    renderDiagram();
  }, [code]);
  
  return (
    <div className="mb-6 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="public-mermaid">
      <div className="p-6 bg-white dark:bg-slate-900 flex items-center justify-center min-h-[100px]">
        {error ? (
          <div className="text-red-500 dark:text-red-400 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded">
            <strong>Diagram Error:</strong> {error}
          </div>
        ) : (
          <div ref={containerRef} className="mermaid-diagram" />
        )}
      </div>
      {caption && (
        <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-400 text-center">{caption}</p>
        </div>
      )}
    </div>
  );
};

export default function PublicDocsPage() {
  const { spaceId, docId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);

  const [space, setSpace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Reader's saved choice wins from the very first render, so there is no flash of
  // the owner's default before the stored preference is applied.
  const [darkMode, setDarkMode] = useState(() => readDocsThemePreference(spaceId) === 'dark');
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [highlightQuery, setHighlightQuery] = useState('');

  const searchAllowed = space?.show_search === true;

  // iframe / embed: ?navbar=0|false or ?hide_navbar=1|true
  const hideNavbar = useMemo(() => {
    const navbar = (searchParams.get('navbar') || '').toLowerCase();
    const hide = (searchParams.get('hide_navbar') || '').toLowerCase();
    return navbar === '0' || navbar === 'false' || hide === '1' || hide === 'true';
  }, [searchParams]);

  // Branding follows the active theme; falls back to the light asset when no dark
  // variant was uploaded, so single-logo spaces behave exactly as before.
  const themedLogoUrl = useMemo(() => resolveSpaceLogoUrl(space, darkMode), [space, darkMode]);
  const themedFaviconUrl = useMemo(() => resolveSpaceFaviconUrl(space, darkMode), [space, darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      writeDocsThemePreference(spaceId, next);
      return next;
    });
  }, [spaceId]);

  // Keep <html class="dark"> in step with state. Doing this from state rather than in
  // the click handler means the stored preference is applied on first paint too, and
  // covers UI that renders in a portal outside the page wrapper.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Keyboard shortcut for search (Ctrl+K or Cmd+K) — only when search is on the plan
  useEffect(() => {
    if (!searchAllowed) return undefined;
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchAllowed]);

  // Apply ?q= highlight only when search is entitled; strip q from URL otherwise
  useEffect(() => {
    if (!space) return;
    const q = searchParams.get('q');
    if (!searchAllowed) {
      setHighlightQuery('');
      setSearchModalOpen(false);
      if (q) {
        const next = new URLSearchParams(searchParams);
        next.delete('q');
        const qs = next.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : '' },
          { replace: true }
        );
      }
      return;
    }
    if (q) {
      setHighlightQuery(q);
    } else {
      setHighlightQuery('');
    }
  }, [space, searchAllowed, searchParams, navigate, location.pathname]);

  // Highlight and scroll to matching content when document loads with a search query
  useEffect(() => {
    if (!searchAllowed || !highlightQuery || !selectedDoc?.content || !contentRef.current) return;

    // Wait for content to render
    const timer = setTimeout(() => {
      highlightAndScrollToMatch(highlightQuery);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAllowed, highlightQuery, selectedDoc?.id]);

  // Function to highlight matching text and scroll to it
  const highlightAndScrollToMatch = (query) => {
    if (!contentRef.current || !query) return;

    // Remove any existing highlights
    const existingMarks = contentRef.current.querySelectorAll('mark.search-highlight');
    existingMarks.forEach(mark => {
      const parent = mark.parentNode;
      parent.replaceChild(document.createTextNode(mark.textContent), mark);
      parent.normalize();
    });

    // Find all text nodes in the content
    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      if (node.textContent.toLowerCase().includes(query.toLowerCase())) {
        textNodes.push(node);
      }
    }

    if (textNodes.length === 0) return;

    // Highlight the first match and scroll to it
    let firstHighlight = null;
    
    textNodes.forEach((textNode, idx) => {
      const text = textNode.textContent;
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      
      if (parts.length <= 1) return;

      const fragment = document.createDocumentFragment();
      parts.forEach(part => {
        if (regex.test(part)) {
          const mark = document.createElement('mark');
          mark.className = 'search-highlight bg-yellow-300 dark:bg-yellow-600 text-inherit rounded px-0.5 py-0.5 transition-all';
          mark.textContent = part;
          if (!firstHighlight) {
            firstHighlight = mark;
            mark.className += ' ring-2 ring-yellow-500 ring-offset-2';
          }
          fragment.appendChild(mark);
          regex.lastIndex = 0; // Reset regex
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    // Scroll to the first highlight with smooth animation
    if (firstHighlight) {
      setTimeout(() => {
        firstHighlight.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  };

  // Clear highlights when navigating away or changing docs
  const clearHighlights = useCallback(() => {
    setHighlightQuery('');
    if (contentRef.current) {
      const marks = contentRef.current.querySelectorAll('mark.search-highlight');
      marks.forEach(mark => {
        const parent = mark.parentNode;
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
    }
  }, []);

  // Handle search result selection
  const handleSearchResultSelect = useCallback(
    (result) => {
      if (!searchAllowed) return;
      const url = result.url.includes('?')
        ? `${result.url}&q=${encodeURIComponent(result.searchQuery)}`
        : `${result.url}?q=${encodeURIComponent(result.searchQuery)}`;
      navigate(url);
    },
    [navigate, searchAllowed]
  );

  // Favicon lives in its own effect so it re-applies when the theme is toggled,
  // not only when the space first loads.
  useEffect(() => {
    if (themedFaviconUrl) applySpaceFavicon(themedFaviconUrl);
  }, [themedFaviconUrl]);

  // Apply custom CSS and JS when space loads
  useEffect(() => {
    if (!space) return;

    // Apply custom CSS
    if (space.custom_css) {
      const styleId = 'docustack-custom-css';
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = space.custom_css;
    }

    // Apply custom JS
    if (space.custom_js) {
      const scriptId = 'docustack-custom-js';
      let scriptEl = document.getElementById(scriptId);
      if (scriptEl) {
        scriptEl.remove();
      }
      scriptEl = document.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.textContent = space.custom_js;
      document.body.appendChild(scriptEl);
    }

    // Set CSS custom properties for colors
    document.documentElement.style.setProperty('--docs-primary', space.primary_color || '#2563eb');
    document.documentElement.style.setProperty('--docs-secondary', space.secondary_color || '#64748b');

    // Cleanup on unmount
    return () => {
      const styleEl = document.getElementById('docustack-custom-css');
      if (styleEl) styleEl.remove();
      const scriptEl = document.getElementById('docustack-custom-js');
      if (scriptEl) scriptEl.remove();
      applyPlatformFavicon();
    };
  }, [space]);

  // Fetch space
  useEffect(() => {
    if (!spaceId) return;
    
    axios.get(`${API_URL}/public/docs/${spaceId}/space`)
      .then(res => {
        setSpace(res.data);
        // The owner's theme is only a default for readers who have never chosen.
        if (readDocsThemePreference(spaceId) === null && res.data.theme === 'dark') {
          setDarkMode(true);
        }
      })
      .catch((err) => {
        // Surface the real reason. A billing-suspended portal answers 402 with an
        // explanatory detail; collapsing every failure into "404 not found" sends
        // people looking for a publishing problem that isn't there.
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        setErrorStatus(status || null);
        setError(detail || 'Documentation not found');
        setLoading(false);
      });
  }, [spaceId]);

  // Fetch documents when space is ready
  useEffect(() => {
    if (!space?.id) return;
    
    axios.get(`${API_URL}/public/docs/${space.id}/documents`)
      .then(res => {
        setDocuments(res.data);
        const exp = {};
        res.data.forEach(d => { exp[d.id] = true; });
        setExpanded(exp);
        
        if (docId) {
          axios.get(`${API_URL}/public/docs/${space.id}/documents/${docId}`)
            .then(r => setSelectedDoc(r.data))
            .catch(console.error);
        } else if (res.data.length > 0) {
          navigate(`/docs/${spaceId}/${res.data[0].id}`, { replace: true });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [space?.id, spaceId, docId, navigate]);

  // Fetch document when docId changes
  useEffect(() => {
    if (!space?.id || !docId) return;
    
    // Clear highlights when changing documents (unless we have a new query)
    if (!searchParams.get('q')) {
      clearHighlights();
    }
    
    axios.get(`${API_URL}/public/docs/${space.id}/documents/${docId}`)
      .then(res => setSelectedDoc(res.data))
      .catch(console.error);
  }, [docId, space?.id, searchParams, clearHighlights]);

  // Track page view
  useEffect(() => {
    if (!selectedDoc?.id) return;
    
    // Track view after a short delay (avoid counting quick navigations)
    const timer = setTimeout(() => {
      axios.post(`${API_URL}/documents/${selectedDoc.id}/views`, {
        referrer: document.referrer || null,
        user_agent: navigator.userAgent || null
      }).catch(console.error);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [selectedDoc?.id]);

  const handleSelectDoc = (id) => {
    clearHighlights();
    navigate(`/docs/${spaceId}/${id}`);
    setSidebarOpen(false);
  };

  const handleToggle = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Render document tree item
  const renderTreeItem = (doc, level = 0) => {
    const hasChildren = doc.children && doc.children.length > 0;
    const isExpanded = expanded[doc.id];
    const isSelected = selectedDoc?.id === doc.id;
    const primaryColor = space?.primary_color || '#2563eb';

    return (
      <div key={doc.id}>
        <button
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left',
            isSelected
              ? 'font-medium'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          )}
          style={isSelected ? { 
            backgroundColor: `${primaryColor}15`,
            color: primaryColor
          } : { paddingLeft: `${12 + level * 16}px` }}
          onClick={() => handleSelectDoc(doc.id)}
          data-testid={`doc-tree-item-${doc.id}`}
        >
          {hasChildren && (
            <span
              onClick={(e) => { e.stopPropagation(); handleToggle(doc.id); }}
              className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded cursor-pointer"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
          {!hasChildren && <FileText className="w-4 h-4 text-slate-400" />}
          <span className="truncate">{doc.title}</span>
        </button>
        {hasChildren && isExpanded && doc.children.map(child => renderTreeItem(child, level + 1))}
      </div>
    );
  };

  // Render document content
  const renderContent = () => {
    if (!selectedDoc?.content) return null;
    
    let blocks = [];
    try {
      const data = JSON.parse(selectedDoc.content);
      blocks = data.blocks || [];
    } catch {
      return <p className="text-slate-600">Unable to render content</p>;
    }

    return blocks.map((block, index) => {
      const key = `block-${index}`;
      
      if (block.type === 'header') {
        const level = block.data?.level || 2;
        const HeadingTag = `h${level}`;
        const cls = {
          1: 'text-4xl font-bold mt-10 mb-6',
          2: 'text-3xl font-bold mt-8 mb-4',
          3: 'text-2xl font-semibold mt-6 mb-3',
          4: 'text-xl font-semibold mt-5 mb-2',
          5: 'text-lg font-medium mt-4 mb-2',
          6: 'text-base font-medium mt-3 mb-2',
        }[level] || 'text-xl font-semibold mt-4 mb-2';
        return <HeadingTag key={key} id={`heading-${index}`} className={cn(cls, 'text-slate-900 dark:text-white')} dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />;
      }
      
      if (block.type === 'paragraph') {
        return <p key={key} className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />;
      }
      
      if (block.type === 'list') {
        const items = block.data?.items || [];
        const Tag = block.data?.style === 'ordered' ? 'ol' : 'ul';
        const listCls = block.data?.style === 'ordered' ? 'list-decimal list-inside' : 'list-disc list-inside';
        return (
          <Tag key={key} className={cn(listCls, 'mb-4 space-y-1 text-slate-700 dark:text-slate-300')}>
            {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: typeof item === 'string' ? item : item.content || '' }} />)}
          </Tag>
        );
      }
      
      // Nested list
      if (block.type === 'nestedList') {
        const renderNestedItems = (items, style) => {
          const Tag = style === 'ordered' ? 'ol' : 'ul';
          const listCls = style === 'ordered' ? 'list-decimal' : 'list-disc';
          return (
            <Tag className={cn(listCls, 'ml-4 space-y-1')}>
              {items.map((item, i) => (
                <li key={i}>
                  <span dangerouslySetInnerHTML={{ __html: item.content || '' }} />
                  {item.items && item.items.length > 0 && renderNestedItems(item.items, style)}
                </li>
              ))}
            </Tag>
          );
        };
        return (
          <div key={key} className="mb-4 text-slate-700 dark:text-slate-300">
            {renderNestedItems(block.data?.items || [], block.data?.style || 'unordered')}
          </div>
        );
      }
      
      // Checklist
      if (block.type === 'checklist') {
        return (
          <div key={key} className="mb-4 space-y-2">
            {(block.data?.items || []).map((item, i) => (
              <label key={i} className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={item.checked} readOnly className="w-4 h-4 rounded border-slate-300" />
                <span className={item.checked ? 'line-through text-slate-400' : ''} dangerouslySetInnerHTML={{ __html: item.text || '' }} />
              </label>
            ))}
          </div>
        );
      }
      
      if (block.type === 'code') {
        return (
          <pre key={key} className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4 mb-4 overflow-x-auto">
            <code className="text-sm text-slate-100 font-mono">{block.data?.code || ''}</code>
          </pre>
        );
      }
      
      // Code with syntax highlighting
      if (block.type === 'codeHighlight' || block.type === 'codeBox') {
        return (
          <pre key={key} className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4 mb-4 overflow-x-auto">
            {block.data?.language && (
              <div className="text-xs text-slate-400 mb-2 uppercase tracking-wide">{block.data.language}</div>
            )}
            <code className="text-sm text-slate-100 font-mono whitespace-pre">{block.data?.code || ''}</code>
          </pre>
        );
      }
      
      // Raw HTML
      if (block.type === 'raw') {
        return <div key={key} className="mb-4" dangerouslySetInnerHTML={{ __html: block.data?.html || '' }} />;
      }
      
      if (block.type === 'quote') {
        return (
          <blockquote key={key} className="border-l-4 border-blue-500 pl-4 italic text-slate-600 dark:text-slate-400 mb-4">
            <p dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />
            {block.data?.caption && <cite className="text-sm not-italic">— {block.data.caption}</cite>}
          </blockquote>
        );
      }
      
      if (block.type === 'callout') {
        const colors = {
          info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 text-blue-800 dark:text-blue-200',
          warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 text-amber-800 dark:text-amber-200',
          success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 text-emerald-800 dark:text-emerald-200',
          danger: 'bg-red-50 dark:bg-red-900/20 border-red-200 text-red-800 dark:text-red-200',
          error: 'bg-red-50 dark:bg-red-900/20 border-red-200 text-red-800 dark:text-red-200',
          note: 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 text-slate-800 dark:text-slate-200',
        };
        return (
          <div key={key} className={cn('rounded-lg border p-4 mb-4', colors[block.data?.type] || colors.info)}>
            {block.data?.title && <p className="font-semibold mb-1">{block.data.title}</p>}
            <p dangerouslySetInnerHTML={{ __html: block.data?.message || block.data?.text || '' }} />
          </div>
        );
      }
      
      if (block.type === 'delimiter') {
        return <hr key={key} className="my-8 border-slate-200 dark:border-slate-700" />;
      }
      
      // Mermaid diagrams - use interactive component
      if (block.type === 'mermaid') {
        return <MermaidDiagram key={key} code={block.data?.code} caption={block.data?.caption} />;
      }
      
      // Toggle/Accordion block
      if (block.type === 'toggle') {
        return (
          <details key={key} className="mb-4 border border-slate-200 dark:border-slate-700 rounded-lg">
            <summary className="px-4 py-3 cursor-pointer font-medium text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800 rounded-t-lg">
              {block.data?.title || 'Toggle'}
            </summary>
            <div className="px-4 py-3 text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: block.data?.content || block.data?.text || '' }} />
          </details>
        );
      }
      
      // AnyButton block
      if (block.type === 'AnyButton' || block.type === 'button') {
        const url = block.data?.link || block.data?.url || '#';
        const text = block.data?.text || 'Button';
        const newTab = block.data?.newTab !== false;
        return (
          <div key={key} className="mb-4">
            <a 
              href={url} 
              target={newTab ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              {text}
            </a>
          </div>
        );
      }
      
      // API Endpoint block
      if (block.type === 'apiEndpoint') {
        const methodColors = {
          GET: 'bg-emerald-100 text-emerald-800',
          POST: 'bg-blue-100 text-blue-800',
          PUT: 'bg-amber-100 text-amber-800',
          PATCH: 'bg-purple-100 text-purple-800',
          DELETE: 'bg-red-100 text-red-800',
        };
        return (
          <div key={key} className="mb-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-800">
              <span className={cn('px-2 py-1 rounded text-xs font-bold', methodColors[block.data?.method] || methodColors.GET)}>
                {block.data?.method || 'GET'}
              </span>
              <code className="text-sm font-mono text-slate-700 dark:text-slate-300">{block.data?.endpoint || '/api/endpoint'}</code>
            </div>
            {block.data?.description && (
              <div className="px-4 py-3 text-slate-600 dark:text-slate-400 text-sm" dangerouslySetInnerHTML={{ __html: block.data.description }} />
            )}
          </div>
        );
      }
      
      // Tabs block - use interactive component
      if (block.type === 'tabs') {
        const tabs = block.data?.tabs || [];
        return <InteractiveTabs key={key} tabs={tabs} />;
      }
      
      // Accordion block - use interactive component (single accordion)
      if (block.type === 'accordion') {
        // Handle both single accordion format and multi-item format
        const items = block.data?.items;
        if (items && Array.isArray(items)) {
          // Multi-item accordion
          return (
            <div key={key}>
              {items.map((item, i) => (
                <InteractiveAccordion key={i} title={item.title} content={item.content} isOpen={item.isOpen} />
              ))}
            </div>
          );
        }
        // Single accordion format
        return <InteractiveAccordion key={key} title={block.data?.title} content={block.data?.content} isOpen={block.data?.isOpen} />;
      }
      
      // Divider block (custom)
      if (block.type === 'divider') {
        const style = block.data?.style || 'solid';
        return <hr key={key} className={cn('my-8 border-slate-200 dark:border-slate-700', `border-${style}`)} />;
      }
      
      // Columns block
      if (block.type === 'columns') {
        const cols = block.data?.columns || [];
        return (
          <div key={key} className="mb-4 grid gap-4" style={{ gridTemplateColumns: `repeat(${cols.length || 2}, 1fr)` }}>
            {cols.map((col, i) => (
              <div key={i} className="text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: col.content || '' }} />
            ))}
          </div>
        );
      }
      
      // Card block
      if (block.type === 'card') {
        const cardContent = (
          <div className="mb-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 hover:shadow-md transition-shadow" data-testid="public-card">
            <div className="flex items-start gap-3">
              {block.data?.icon && <span className="text-2xl">{block.data.icon}</span>}
              <div className="flex-1">
                {block.data?.title && <h4 className="font-semibold text-slate-900 dark:text-white mb-2">{block.data.title}</h4>}
                <div className="text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: block.data?.content || block.data?.text || '' }} />
              </div>
            </div>
          </div>
        );
        
        // If card has a link in read-only/public view, wrap in anchor
        if (block.data?.link) {
          return <a key={key} href={block.data.link} target="_blank" rel="noopener noreferrer" className="block">{cardContent}</a>;
        }
        return <div key={key}>{cardContent}</div>;
      }
      
      // Steps block - improved styling with description
      if (block.type === 'steps') {
        const steps = block.data?.steps || [];
        return (
          <div key={key} className="mb-6 relative" data-testid="public-steps">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-4 pb-6 last:pb-0">
                {/* Step indicator with connecting line */}
                <div className="flex flex-col items-center">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
                    {i + 1}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 w-0.5 bg-gradient-to-b from-blue-400 to-blue-200 dark:from-blue-600 dark:to-blue-800 mt-2" />
                  )}
                </div>
                {/* Step content */}
                <div className="flex-1 pt-1 pb-2">
                  {step.title && (
                    <h4 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">{step.title}</h4>
                  )}
                  {(step.description || step.content) && (
                    <div 
                      className="text-slate-600 dark:text-slate-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: step.description || step.content || '' }} 
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      }
      
      // Parameter block (API parameter)
      if (block.type === 'parameter') {
        return (
          <div key={key} className="mb-2 flex items-start gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
            <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono text-blue-600 dark:text-blue-400">
              {block.data?.name || 'param'}
            </code>
            <span className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-slate-500">
              {block.data?.type || 'string'}
            </span>
            {block.data?.required && <span className="text-xs text-red-500">required</span>}
            <span className="flex-1 text-sm text-slate-600 dark:text-slate-400">{block.data?.description || ''}</span>
          </div>
        );
      }
      
      // Badge block
      if (block.type === 'badge') {
        const colors = {
          blue: 'bg-blue-100 text-blue-800',
          green: 'bg-emerald-100 text-emerald-800',
          yellow: 'bg-amber-100 text-amber-800',
          red: 'bg-red-100 text-red-800',
          purple: 'bg-purple-100 text-purple-800',
          gray: 'bg-slate-100 text-slate-800',
        };
        return (
          <span key={key} className={cn('inline-block px-2 py-1 rounded text-sm font-medium mr-2 mb-2', colors[block.data?.color] || colors.blue)}>
            {block.data?.text || 'Badge'}
          </span>
        );
      }
      
      // Tooltip block - render as underlined text with title
      if (block.type === 'tooltip') {
        return (
          <span key={key} className="underline decoration-dotted cursor-help" title={block.data?.tooltip || ''}>
            {block.data?.text || ''}
          </span>
        );
      }
      
      // Image block
      if (block.type === 'image') {
        const url = block.data?.url;
        const caption = block.data?.caption;
        const stretched = block.data?.stretched;
        if (!url) return null;
        return (
          <figure key={key} className={cn('mb-6', stretched ? 'w-full' : 'max-w-2xl mx-auto')}>
            <img src={url} alt={block.data?.alt || caption || ''} className="rounded-lg w-full" />
            {caption && <figcaption className="mt-2 text-sm text-center text-slate-500">{caption}</figcaption>}
          </figure>
        );
      }
      
      // Video block (YouTube, Vimeo, or direct)
      if (block.type === 'video') {
        const url = block.data?.url;
        if (!url) return null;
        return (
          <VideoBlockPreview key={key} url={url} caption={block.data?.caption} />
        );
      }
      
      // Embed block (iframes)
      if (block.type === 'embed') {
        const url = block.data?.url;
        const caption = block.data?.caption;
        if (!url) return null;
        return (
          <div key={key} className="mb-6">
            <div className="aspect-video">
              <iframe src={url} className="w-full h-full rounded-lg border border-slate-200 dark:border-slate-700" allowFullScreen title="Embedded content" />
            </div>
            {caption && <p className="mt-2 text-sm text-center text-slate-500">{caption}</p>}
          </div>
        );
      }
      
      // File block
      if (block.type === 'file') {
        const url = block.data?.url;
        const filename = block.data?.filename || block.data?.name || url?.split('/').pop() || 'Download file';
        if (!url) return null;
        return (
          <FileBlockPreview
            key={key}
            url={url}
            filename={filename}
            size={block.data?.size}
            variant="public"
          />
        );
      }
      
      // Table block
      if (block.type === 'table') {
        const content = block.data?.content || [];
        if (content.length === 0) return null;
        return (
          <div key={key} className="mb-6 overflow-x-auto">
            <table className="w-full border-collapse border border-slate-200 dark:border-slate-700">
              <tbody>
                {content.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex === 0 && block.data?.withHeadings ? 'bg-slate-50 dark:bg-slate-800' : ''}>
                    {row.map((cell, cellIndex) => {
                      const Tag = rowIndex === 0 && block.data?.withHeadings ? 'th' : 'td';
                      return (
                        <Tag key={cellIndex} className="border border-slate-200 dark:border-slate-700 px-4 py-2 text-left" dangerouslySetInnerHTML={{ __html: cell }} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      
      // Warning block
      if (block.type === 'warning') {
        return (
          <div key={key} className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            {block.data?.title && <p className="font-semibold text-amber-800 dark:text-amber-200 mb-1">{block.data.title}</p>}
            <p className="text-amber-700 dark:text-amber-300" dangerouslySetInnerHTML={{ __html: block.data?.message || '' }} />
          </div>
        );
      }
      
      // Alert block  
      if (block.type === 'alert') {
        const types = {
          info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
          warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
          success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200',
          error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
        };
        // Support both 'text' and 'message' fields for compatibility
        const content = block.data?.text || block.data?.message || '';
        return (
          <div key={key} className={cn('mb-4 p-4 border rounded-lg', types[block.data?.type] || types.info)} data-testid="public-alert">
            <p dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        );
      }
      
      // Link tool output
      if (block.type === 'linkTool') {
        const meta = block.data?.meta || {};
        return (
          <a key={key} href={block.data?.link || '#'} target="_blank" rel="noopener noreferrer" className="block mb-4 p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            {meta.image?.url && <img src={meta.image.url} alt="" className="w-full h-32 object-cover rounded mb-3" />}
            <p className="font-medium text-slate-900 dark:text-white">{meta.title || block.data?.link}</p>
            {meta.description && <p className="text-sm text-slate-500 mt-1">{meta.description}</p>}
          </a>
        );
      }
      
      // Fallback for unknown blocks - render raw JSON for debugging (in dev) or nothing
      console.log('Unknown block type:', block.type, block.data);
      return null;
    });
  };

  // Get headings for ToC
  const getHeadings = () => {
    if (!selectedDoc?.content) return [];
    try {
      const data = JSON.parse(selectedDoc.content);
      return (data.blocks || [])
        .map((block, index) => block.type === 'header' ? {
          id: `heading-${index}`,
          text: (block.data?.text || '').replace(/<[^>]+>/g, ''),
          level: block.data?.level || 2
        } : null)
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', darkMode ? 'dark bg-slate-950' : 'bg-white')}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', darkMode ? 'dark bg-slate-950' : 'bg-white')}>
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{errorStatus || 404}</h1>
          <p className="text-slate-600 dark:text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  const headings = getHeadings();
  const headerOffset = hideNavbar ? 'top-12' : 'top-16';
  const sidebarHeight = hideNavbar ? 'h-[calc(100vh-3rem)]' : 'h-[calc(100vh-4rem)]';

  return (
    <div className={cn('min-h-screen', darkMode ? 'dark' : '')} data-testid="public-docs-page">
      <div className="bg-white dark:bg-slate-950 min-h-screen">
        {/* Header */}
        <header
          className={cn(
            'sticky top-0 z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800',
            hideNavbar && 'border-b-0'
          )}
          data-testid="public-docs-header"
        >
          <div className={cn('max-w-screen-2xl mx-auto px-4 flex items-center justify-between', hideNavbar ? 'h-12' : 'h-16')}>
            <div className="flex items-center gap-4">
              <button
                className={cn(
                  'p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg',
                  !hideNavbar && 'lg:hidden'
                )}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-toggle"
                aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              {!hideNavbar && (
                <div className="flex items-center gap-3" data-testid="public-docs-logo">
                  {themedLogoUrl ? (
                    <img src={themedLogoUrl} alt={space.name} className="h-8" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: space?.primary_color || '#2563eb' }}>
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <span className="font-semibold text-slate-900 dark:text-white" data-testid="public-docs-title">{space?.meta_title || space?.name}</span>
                </div>
              )}
            </div>

            {!hideNavbar && (
            <div className="flex items-center gap-3">
              {searchAllowed && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white sm:h-10 sm:w-64 sm:rounded-md sm:justify-start sm:gap-2 sm:px-3"
                  onClick={() => searchAllowed && setSearchModalOpen(true)}
                  data-testid="search-trigger-button"
                  aria-label="Search docs"
                >
                  <Search className="h-4 w-4 shrink-0" />
                  <span className="hidden flex-1 text-left sm:inline">Search docs...</span>
                  <kbd className="ml-auto hidden items-center gap-1 rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-800 sm:inline-flex">
                    <Command className="h-3 w-3" />K
                  </kbd>
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} data-testid="public-docs-theme-toggle">
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
            )}
          </div>
        </header>

        <div className="flex w-full">
          {/* Sidebar */}
          <aside
            className={cn(
              'fixed left-0 z-30 flex w-72 flex-col border-r border-slate-200 bg-white transition-transform dark:border-slate-800 dark:bg-slate-950',
              headerOffset,
              sidebarHeight,
              hideNavbar
                ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
                : cn('lg:sticky lg:translate-x-0', sidebarOpen ? 'translate-x-0' : '-translate-x-full')
            )}
            data-testid="public-docs-sidebar"
          >
            <div className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="min-h-0 flex-1 py-4">
                <nav className="space-y-1 px-3" data-testid="public-docs-nav">
                  {documents.map((doc) => renderTreeItem(doc, 0))}
                </nav>
              </ScrollArea>
              <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950">
                <a
                  href="https://doc-tree.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-[11px] leading-snug text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  data-testid="public-docs-powered-by"
                >
                  Powered by{' '}
                  <span className="font-semibold text-slate-700 dark:text-slate-300">DocTree</span>
                </a>
              </div>
            </div>
          </aside>

          {sidebarOpen && (
            <div
              className={cn('fixed inset-0 bg-black/50 z-20', !hideNavbar && 'lg:hidden')}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-testid="public-docs-main">
            <div className="px-6 py-8">
              {selectedDoc ? (
                <div data-testid="public-docs-content" ref={contentRef}>
                  <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-8" data-testid="public-docs-doc-title">{selectedDoc.title}</h1>
                  
                  {/* Highlight indicator */}
                  {searchAllowed && highlightQuery && (
                    <div className="mb-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-yellow-800 dark:text-yellow-200">
                        Showing results for "<strong>{highlightQuery}</strong>"
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHighlights}
                        className="text-yellow-700 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-yellow-100"
                        data-testid="clear-highlights-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  )}
                  
                  <div className="doc-tree-rich prose dark:prose-invert max-w-none break-words overflow-wrap-anywhere" data-testid="public-docs-doc-body">{renderContent()}</div>
                  {selectedDoc.updated_at && <p className="mt-12 text-sm text-slate-500" data-testid="public-docs-updated-at">Last updated: {new Date(selectedDoc.updated_at).toLocaleDateString()}</p>}
                  
                  {/* Article Feedback */}
                  {selectedDoc.show_feedback_panel !== false && (
                    <div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
                      <DocumentFeedback documentId={selectedDoc.id} isPublic={true} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-16" data-testid="public-docs-empty-state">
                  <FileText className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <h2 className="text-xl font-medium text-slate-900 dark:text-white mb-2">Select a document</h2>
                  <p className="text-slate-600 dark:text-slate-400">Choose a document from the sidebar.</p>
                </div>
              )}
            </div>
          </main>

          {/* Table of Contents */}
          {space?.show_toc && headings.length > 0 && (
            <div className="hidden xl:block w-64 flex-shrink-0" data-testid="public-docs-toc">
              <div className="sticky top-24 p-4">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">On this page</h4>
                <nav className="space-y-1" data-testid="public-docs-toc-nav">
                  {headings.map(h => (
                    <a 
                      key={h.id} 
                      href={`#${h.id}`} 
                      className={cn(
                        'block text-sm py-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white truncate max-w-full',
                        h.level === 2 ? 'pl-0' : h.level === 3 ? 'pl-3' : 'pl-6'
                      )}
                      title={h.text}
                    >
                      {h.text.length > 40 ? h.text.substring(0, 40) + '...' : h.text}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          )}
        </div>

        {space?.footer_text && (
          <footer className="border-t border-slate-200 dark:border-slate-800 py-6 text-center text-sm text-slate-500" data-testid="public-docs-footer">{space.footer_text}</footer>
        )}
      </div>

      {searchAllowed ? (
        <PublicSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          spaceId={space?.id}
          spaceName={space?.name}
          onSelectResult={handleSearchResultSelect}
        />
      ) : null}
    </div>
  );
}
